import { useProductApi } from "@/hooks/apiHooks/useProductApi";
import { useMountRead } from "@/hooks/useMountRead";
import { Input } from "@common/Form/Input";
import { Table } from "@common/Table";
import { ComponentColor, ComponentSize } from "@common/types";
import { faMagnifyingGlass, faPen } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    DEFAULT_PRODUCTS_QUERY,
    GetProductsSortOptionType,
    SortDirectionType,
} from "@models/productFilter";
import { useCallback, useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAtomValue, useSetAtom } from "jotai";
import {
    productsAtom,
    isFetchingProductsAtom,
} from "@/store/productAtoms";
import type { GetProductsQuery } from "@models/productFilter";
import type { Product } from "@models/product";
import type { ColumnDef } from "@tanstack/react-table";
import { currencyFormatter } from "@utils/formatter";
import { vendorRowsForSku } from "@utils/vendorRows";
import { Badge, Button, Title, ErrorState } from "@common/index";
import { ROUTES } from "@routing/routes";
import { isManageOptionsOpenAtom } from "@/store/manageOptionsAtoms";
import { ManageOptionsModal } from "./ManageOptions/ManageOptionsModal";
import { derivedAmount } from "./_helpers/derivedAmount";

export const AdminInventoryProducts = () => {
    const { getProducts } = useProductApi();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const setManageOptionsOpen = useSetAtom(isManageOptionsOpenAtom);

    const products = useAtomValue(productsAtom);
    const isFetching = useAtomValue(isFetchingProductsAtom);

    // debounce search to avoid too many API calls
    const [searchQuery, setSearchQuery] = useState<string>(
        searchParams.get("search") || "",
    );

    const currentPage =
        Number(searchParams.get("page")) ||
        DEFAULT_PRODUCTS_QUERY.pageNumber ||
        1;

    const currentPageSize =
        Number(searchParams.get("pageSize")) ||
        DEFAULT_PRODUCTS_QUERY.pageSize ||
        25;

    const searchTerm = searchParams.get("search") || "";

    // #294: deactivated products are only reachable through this list (their
    // edit page is where they are reactivated), but reactivation is rare and
    // the default read stays active-only — `?includeInactive=1` opts in, and
    // the Status column tells the rows apart once it does.
    const includeInactive = searchParams.get("includeInactive") === "1";

    const sortBy = searchParams.get("sortBy");
    const sortDirection = searchParams.get("sortDirection");

    // The Table shows this sort (controlled), so its header follows the URL.
    const sorting =
        sortBy && sortDirection
            ? [{ id: sortBy.toLowerCase(), desc: sortDirection === "desc" }]
            : [];

    const columns = useMemo<ColumnDef<Product>[]>(
        () => [
            {
                accessorKey: "name",
                header: "Item",
                enableSorting: true,
                cell: ({ row }) => (
                    <div className="flex items-center gap-3">
                        <div className="avatar">
                            <div className="mask mask-squircle h-12 w-12">
                                <img
                                    src={row.original.images[0]?.url}
                                    alt={row.original.name}
                                />
                            </div>
                        </div>
                        <div>
                            <div className="font-bold text-nowrap">
                                {row.original.name}
                            </div>
                            {/* A product made of variants has no base sku
                                (§3.1, BR-04), so no SKU line. */}
                            {row.original.sku && (
                                <div className="text-sm opacity-50 text-nowrap">
                                    SKU: {row.original.sku}
                                </div>
                            )}
                        </div>
                    </div>
                ),
                size: 500,
            },
            {
                accessorKey: "categoryName",
                header: "Category",
                enableSorting: false,
            },
            // With "Include inactive" ticked (#294) this is what tells the
            // rows apart — the list is the only route to a deactivated
            // product's edit page, where it can be reactivated.
            {
                accessorKey: "isActive",
                header: "Status",
                enableSorting: false,
                cell: ({ getValue }) =>
                    getValue() ? (
                        <Badge
                            color={ComponentColor.Success}
                            size={ComponentSize.SM}
                        >
                            Active
                        </Badge>
                    ) : (
                        <Badge
                            color={ComponentColor.Neutral}
                            size={ComponentSize.SM}
                        >
                            Inactive
                        </Badge>
                    ),
            },
            {
                accessorKey: "price",
                header: "Price",
                // P1: a product no vendor offer prices has no price ("—"),
                // not $0.00.
                cell: ({ getValue, row }) =>
                    derivedAmount(
                        getValue() as number,
                        row.original.hasOffers,
                    ) ?? "—",
                enableSorting: false,
            },
            {
                accessorKey: "msrp",
                header: "MSRP",
                cell: ({ getValue }) =>
                    currencyFormatter.format(getValue() as number),
                enableSorting: false,
            },
            {
                accessorKey: "cost",
                header: "Cost",
                cell: ({ getValue, row }) =>
                    derivedAmount(
                        getValue() as number,
                        row.original.hasOffers,
                    ) ?? "—",
                enableSorting: false,
            },
            {
                accessorKey: "vendors",
                header: "Vendor",
                // The distinct names of the ACTIVE rows supplying the BASE sku.
                // `vendors[]` is not filtered server-side (soft-deleted rows
                // carry isActive: false), and once #390 widens it to every
                // supply row of the product, the variants' rows would otherwise
                // be named — and repeated — in a column that reads as the
                // product's own supplier.
                cell: ({ getValue }) =>
                    Array.from(
                        new Set(
                            vendorRowsForSku(
                                getValue() as Product["vendors"],
                                null,
                            )
                                .filter((vendor) => vendor.isActive)
                                .map((vendor) => vendor.vendorName),
                        ),
                    ).join(", "),
                enableSorting: false,
            },
            {
                id: "actions",
                header: "",
                cell: ({ row }) => (
                    <button
                        type="button"
                        className="btn btn-sm text-primary"
                        aria-label={`Edit ${row.original.name}`}
                        onClick={() =>
                            navigate(
                                ROUTES.ADMIN_PRODUCTS_EDIT(row.original.id),
                            )
                        }
                    >
                        <FontAwesomeIcon icon={faPen} />
                        Edit
                    </button>
                ),
            },
        ],
        [navigate],
    );

    useEffect(() => {
        if (searchQuery !== searchTerm) {
            const timer = setTimeout(() => {
                const params: Record<string, string> = {
                    page: DEFAULT_PRODUCTS_QUERY.pageNumber!.toString(),
                    pageSize: currentPageSize.toString(),
                };
                if (searchQuery) {
                    params.search = searchQuery;
                }
                if (includeInactive) {
                    params.includeInactive = "1";
                }
                setSearchParams(params);
            }, 500);

            return () => clearTimeout(timer);
        }
    }, [
        searchQuery,
        searchTerm,
        currentPageSize,
        includeInactive,
        setSearchParams,
    ]);

    const fetchProducts = useCallback(() => {
        const query: GetProductsQuery = {
            ...DEFAULT_PRODUCTS_QUERY,
            pageNumber: currentPage,
            pageSize: currentPageSize,
            search: searchTerm || undefined,
            includeInactive,
        };

        // only add sorting if it's in the URL and not default
        if (sortBy && sortDirection) {
            // TODO: fix sorting in api to allow other columns
            const sortByMapping: Record<string, GetProductsSortOptionType> = {
                name: GetProductsSortOptionType.Name,
                id: GetProductsSortOptionType.Id,
                description: GetProductsSortOptionType.Description,
                upc: GetProductsSortOptionType.Upc,
                brand: GetProductsSortOptionType.Brand,
            };

            const normalizedSortBy = sortBy.trim().toLowerCase();
            const sortByEnum =
                sortByMapping[normalizedSortBy] || GetProductsSortOptionType.Id;

            const sortDirectionEnum =
                sortDirection === "desc"
                    ? SortDirectionType.Desc
                    : SortDirectionType.Asc;

            query.sortBy = [sortByEnum];
            query.sortDirection = sortDirectionEnum;
        }

        return getProducts(query);
    }, [
        currentPage,
        currentPageSize,
        searchTerm,
        includeInactive,
        sortBy,
        sortDirection,
        getProducts,
    ]);

    // Issued on mount and on every query change; the page shows a failure
    // only for its own read (#407).
    const { readStatus, reload } = useMountRead(fetchProducts);

    const handlePageChange = (page: number) => {
        const params: Record<string, string> = {
            page: page.toString(),
            pageSize: currentPageSize.toString(),
        };
        if (searchTerm) {
            params.search = searchTerm;
        }
        if (includeInactive) {
            params.includeInactive = "1";
        }
        setSearchParams(params);
    };

    const handlePageSizeChange = (newPageSize: number) => {
        const params: Record<string, string> = {
            page: DEFAULT_PRODUCTS_QUERY.pageNumber!.toString(),
            pageSize: newPageSize.toString(),
        };
        if (searchTerm) {
            params.search = searchTerm;
        }
        if (includeInactive) {
            params.includeInactive = "1";
        }
        setSearchParams(params);
    };

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(event.target.value);
    };

    // A filter change resets ?page like the others do.
    const handleIncludeInactiveChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const params: Record<string, string> = {
            page: DEFAULT_PRODUCTS_QUERY.pageNumber!.toString(),
            pageSize: currentPageSize.toString(),
        };
        if (searchTerm) {
            params.search = searchTerm;
        }
        if (event.target.checked) {
            params.includeInactive = "1";
        }
        // Symmetric with handleSortingChange, which preserves includeInactive:
        // toggling the filter must not silently drop an applied sort.
        if (sortBy && sortDirection) {
            params.sortBy = sortBy;
            params.sortDirection = sortDirection;
        }
        setSearchParams(params);
    };

    const handleSortingChange = (
        columnId: string | null,
        direction: "asc" | "desc" | null,
    ) => {
        const params: Record<string, string> = {
            page: currentPage.toString(),
            pageSize: currentPageSize.toString(),
        };

        if (searchTerm) {
            params.search = searchTerm;
        }

        if (includeInactive) {
            params.includeInactive = "1";
        }

        if (columnId && direction) {
            params.sortBy =
                columnId.charAt(0).toUpperCase() + columnId.slice(1);
            params.sortDirection = direction;
        }

        setSearchParams(params);
    };

    return (
        <div className="px-10">
            <div className="grid grid-cols-12 gap-4">
                <Title>Manage Products</Title>
                <div className="col-span-1 mt-1">
                    <Button
                        className="px-4"
                        color={ComponentColor.Secondary}
                        size={ComponentSize.SM}
                        to={ROUTES.ADMIN_PRODUCTS_NEW}
                    >
                        + New
                    </Button>
                </div>
                <div className="col-span-7">
                    <div className="flex justify-end items-center gap-2">
                        <label className="flex items-center gap-2 text-neutral text-nowrap">
                            <input
                                type="checkbox"
                                className="checkbox checkbox-primary checkbox-sm"
                                checked={includeInactive}
                                onChange={handleIncludeInactiveChange}
                            />
                            <span>Include inactive</span>
                        </label>
                        <Button
                            color={ComponentColor.Secondary}
                            size={ComponentSize.SM}
                            onClick={() => setManageOptionsOpen(true)}
                        >
                            Manage Options
                        </Button>
                        <Input
                            value={searchQuery}
                            name="productSearch"
                            placeholder="Search products..."
                            icon={faMagnifyingGlass}
                            type="search"
                            onChange={handleSearch}
                        />
                    </div>
                </div>
            </div>

            <ManageOptionsModal />

            {readStatus === "failed" ? (
                <ErrorState onRetry={reload} />
            ) : (
                <Table
                    data={products.items}
                    columns={columns}
                    size={ComponentSize.XS}
                    pagination={true}
                    isLoading={isFetching || readStatus === "loading"}
                    sortingProps={{
                        onSortingChange: handleSortingChange,
                        sorting,
                    }}
                    paginationProps={{
                        currentPage: products.pageNumber,
                        totalPages: products.totalPages,
                        pageSize: currentPageSize,
                        pageSizeOptions: [10, 25, 50, 100],
                        count: products.count,
                        allCount: products.allCount,
                        onPageChange: handlePageChange,
                        onPageSizeChange: handlePageSizeChange,
                        hasPreviousPage: products.hasPreviousPage,
                        hasNextPage: products.hasNextPage,
                    }}
                />
            )}
            {/* Probably worth adding later */}
            {/* <tfoot>
                    <tr className="text-neutral">
                        <th>Item</th>
                        <th>Category</th>
                        <th>QTY</th>
                        <th>Reorder</th>
                        <th>Price</th>
                        <th>MSRP</th>
                        <th>Cost</th>
                        <th>Vendor</th>
                    </tr>
                </tfoot> */}
        </div>
    );
};
